import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { LocalShipping, Store, Weekend } from "@mui/icons-material";
import { StringField } from "../ConfigFields/StringField";
import { NumberField } from "../ConfigFields/NumberField";
import { BooleanField } from "../ConfigFields/BooleanField";
import { ArrayObjectField } from "../ConfigFields/ArrayObjectField";
import { useAuth } from "../../../../hooks/AuthProvider";
import { WOOCOMMERCE_STATUS_ENDPOINT } from "../../../../config/urls";

const METHOD_ICONS = {
  free: Store,
  calculated: LocalShipping,
  surcharge: Weekend,
};

const DELIVERY_SOURCE_OPTIONS = [
  { value: "woocommerce", label: "WooCommerce" },
  { value: "custom", label: "Custom" },
];

const chargeLabel = (method, standardCharge) => {
  if (method.charge_type === "free") return "Free";
  if (method.charge_type === "surcharge") {
    return `$${standardCharge} + $${method.surcharge ?? 0}`;
  }
  return `$${standardCharge}`;
};

// Tenants configured before shipping_methods was introduced keep this fixed
// pickup/standard/weekend layout untouched unless they migrate to the list below.
const LegacyDeliveryMethodPreset = ({ schema, config, handleConfigChange }) => {
  const standardCharge = config["standard_charge_placeholder"] ?? "—";
  const weekendSurcharge = config["weekend_surcharge"] ?? 8;
  const pickupLabel = config["pickup_button_label"] || "Free Local Pickup";
  const standardLabel = config["standard_button_label"] || "Standard Delivery";
  const weekendLabel = config["weekend_button_label"] || "Sat/Sun Delivery";

  return (
    <Stack spacing={3}>
      <StringField
        fieldKey="message_text"
        schema={schema?.properties["message_text"] || {}}
        value={config["message_text"]}
        required={false}
        onChange={(val) => handleConfigChange("message_text", val)}
      />

      <BooleanField
        fieldKey="show_pincode_in_message"
        schema={schema?.properties["show_pincode_in_message"] || {}}
        value={config["show_pincode_in_message"] ?? true}
        onChange={(val) => handleConfigChange("show_pincode_in_message", val)}
      />

      <Divider />

      <Box>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          Option 1 — Free Pickup
        </Typography>
        <Stack spacing={2}>
          <StringField
            fieldKey="pickup_button_label"
            schema={schema?.properties["pickup_button_label"] || {}}
            value={config["pickup_button_label"]}
            required={false}
            onChange={(val) => handleConfigChange("pickup_button_label", val)}
          />
          <StringField
            fieldKey="pickup_address"
            schema={schema?.properties["pickup_address"] || {}}
            value={config["pickup_address"]}
            required={true}
            onChange={(val) => handleConfigChange("pickup_address", val)}
          />
        </Stack>
      </Box>

      <Divider />

      <Box>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          Option 2 — Standard Delivery
        </Typography>
        <StringField
          fieldKey="standard_button_label"
          schema={schema?.properties["standard_button_label"] || {}}
          value={config["standard_button_label"]}
          required={false}
          onChange={(val) => handleConfigChange("standard_button_label", val)}
        />
      </Box>

      <Divider />

      <Box>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          Option 3 — Weekend Delivery
        </Typography>
        <Stack spacing={2}>
          <StringField
            fieldKey="weekend_button_label"
            schema={schema?.properties["weekend_button_label"] || {}}
            value={config["weekend_button_label"]}
            required={false}
            onChange={(val) => handleConfigChange("weekend_button_label", val)}
          />
          <NumberField
            fieldKey="weekend_surcharge"
            schema={schema?.properties["weekend_surcharge"] || {}}
            value={config["weekend_surcharge"]}
            required={true}
            onChange={(val) => handleConfigChange("weekend_surcharge", val)}
          />
        </Stack>
      </Box>

      <Divider />

      <Box>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          Preview
        </Typography>
        <Stack spacing={1}>
          <Card variant="outlined">
            <CardContent sx={{ py: 1, "&:last-child": { pb: 1 } }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Store fontSize="small" color="success" />
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {pickupLabel}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    No shipping charges
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Card variant="outlined">
            <CardContent sx={{ py: 1, "&:last-child": { pb: 1 } }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <LocalShipping fontSize="small" color="primary" />
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {standardLabel} — ${standardCharge}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Delivered in 3-5 business days
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Card variant="outlined">
            <CardContent sx={{ py: 1, "&:last-child": { pb: 1 } }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Weekend fontSize="small" color="warning" />
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {weekendLabel} — ${standardCharge} + ${weekendSurcharge}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Delivered on Saturday or Sunday
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Stack>
      </Box>
    </Stack>
  );
};

// Everything below is the Custom delivery_source branch — unchanged from
// before delivery_source existed, just renamed and now only rendered when
// that dropdown (see DeliveryMethodPreset below) is set to "custom".
const CustomDeliveryMethodPreset = ({ schema, config, handleConfigChange }) => {
  const shippingMethods = config["shipping_methods"];
  const hasLegacyConfig =
    !Array.isArray(shippingMethods) &&
    (config["pickup_address"] !== undefined || config["weekend_surcharge"] !== undefined);

  // Nodes saved before shipping_methods existed keep their original fixed
  // pickup/standard/weekend editor and runtime behavior untouched. Brand new
  // nodes (no config yet either way) get the new configurable list.
  if (hasLegacyConfig) {
    return (
      <LegacyDeliveryMethodPreset
        schema={schema}
        config={config}
        handleConfigChange={handleConfigChange}
      />
    );
  }

  const methods = Array.isArray(shippingMethods) ? shippingMethods : [];
  const standardCharge = config["standard_charge_placeholder"] ?? "—";

  return (
    <Stack spacing={3}>
      <StringField
        fieldKey="message_text"
        schema={schema?.properties["message_text"] || {}}
        value={config["message_text"]}
        required={false}
        onChange={(val) => handleConfigChange("message_text", val)}
      />

      <BooleanField
        fieldKey="show_pincode_in_message"
        schema={schema?.properties["show_pincode_in_message"] || {}}
        value={config["show_pincode_in_message"] ?? true}
        onChange={(val) => handleConfigChange("show_pincode_in_message", val)}
      />

      <StringField
        fieldKey="pickup_address"
        schema={schema?.properties["pickup_address"] || {}}
        value={config["pickup_address"]}
        required={false}
        onChange={(val) => handleConfigChange("pickup_address", val)}
      />

      <Divider />

      <Box>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          Delivery Methods (1-5)
        </Typography>
        <ArrayObjectField
          label=""
          itemLabel="Option"
          value={methods}
          schema={schema?.properties["shipping_methods"] || {}}
          onChange={(val) => handleConfigChange("shipping_methods", val)}
        />
      </Box>

      <Divider />

      <Box>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          Preview
        </Typography>
        <Stack spacing={1}>
          {methods.map((method, index) => {
            const Icon = METHOD_ICONS[method.charge_type] || LocalShipping;
            return (
              <Card variant="outlined" key={index}>
                <CardContent sx={{ py: 1, "&:last-child": { pb: 1 } }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Icon fontSize="small" color="primary" />
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {method.label || "Delivery option"} —{" "}
                        {chargeLabel(method, standardCharge)}
                      </Typography>
                      {method.description && (
                        <Typography variant="caption" color="text.secondary">
                          {method.description}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      </Box>
    </Stack>
  );
};

// Named pickup branches (e.g. "Walton-on-Thames", "Weybridge") asked as a
// follow-up once the customer picks WooCommerce's Click & Collect option.
// WooCommerce has no API for these at all — they come from a custom
// checkout-page code snippet on the tenant's site — so this is the only
// place they're configured. Editable here so an address/phone update
// doesn't need a deployment.
const CollectionBranchesEditor = ({ schema, config, handleConfigChange }) => {
  const branches = Array.isArray(config["collection_branches"])
    ? config["collection_branches"]
    : [];

  return (
    <Box>
      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
        Collection Branches
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
        Shown as a follow-up question after the customer selects Click & Collect. Leave empty to
        just show the single WooCommerce store address instead.
      </Typography>
      <ArrayObjectField
        label=""
        itemLabel="Branch"
        value={branches}
        schema={schema?.properties["collection_branches"] || {}}
        onChange={(val) => handleConfigChange("collection_branches", val)}
      />
    </Box>
  );
};

const DeliveryMethodPreset = ({ schema, config, handleConfigChange }) => {
  const { getValidToken } = useAuth();
  const tenant = localStorage.getItem("tenant");

  const deliverySource = config?.delivery_source || "custom";
  const [wooCommerceStatus, setWooCommerceStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);

  useEffect(() => {
    if (deliverySource !== "woocommerce") return;

    let cancelled = false;

    const fetchStatus = async () => {
      try {
        const token = await getValidToken();
        const response = await fetch(WOOCOMMERCE_STATUS_ENDPOINT, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "X-Tenant-Id": tenant,
          },
        });

        if (!response.ok) throw new Error("Failed to fetch WooCommerce status");

        const data = await response.json();
        if (!cancelled) setWooCommerceStatus(data);
      } catch (err) {
        console.error("Failed to fetch WooCommerce integration status:", err);
        if (!cancelled) setWooCommerceStatus({ connected: false });
      } finally {
        if (!cancelled) setStatusLoading(false);
      }
    };

    fetchStatus();
    return () => {
      cancelled = true;
    };
  }, [deliverySource, tenant]);

  return (
    <Stack spacing={3}>
      <FormControl fullWidth size="small">
        <InputLabel id="delivery-source-label">Delivery Source</InputLabel>
        <Select
          labelId="delivery-source-label"
          label="Delivery Source"
          value={deliverySource}
          onChange={(e) => handleConfigChange("delivery_source", e.target.value)}
        >
          {DELIVERY_SOURCE_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {deliverySource === "woocommerce" && (
        <Box>
          {statusLoading ? (
            <CircularProgress size={20} />
          ) : wooCommerceStatus?.connected ? (
            <Alert severity="success">
              WooCommerce is integrated{wooCommerceStatus.site_url ? ` (${wooCommerceStatus.site_url})` : ""}.
              The customer will be shown the actual delivery methods configured on your WooCommerce
              store (e.g. Standard Nationwide Delivery, Click & Collect) with their real prices, instead
              of a manually configured list.
            </Alert>
          ) : (
            <Alert severity="warning">
              WooCommerce is not integrated yet. Please integrate WooCommerce from the Integrations
              page to show live delivery methods here.
            </Alert>
          )}
        </Box>
      )}

      {deliverySource === "woocommerce" && (
        <CollectionBranchesEditor
          schema={schema}
          config={config}
          handleConfigChange={handleConfigChange}
        />
      )}

      {deliverySource === "custom" && (
        <CustomDeliveryMethodPreset
          schema={schema}
          config={config}
          handleConfigChange={handleConfigChange}
        />
      )}
    </Stack>
  );
};

export default DeliveryMethodPreset;
